"use client";
import {
  formatAmountCents,
  PaymentMethod,
  PaymentRail,
  BettorBasic,
} from "common";
import React, { createContext, useContext, useState } from "react";
import { loadStripe, StripeElementsOptionsMode } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useAuth, useUser } from "@clerk/clerk-react";
import Link from "next/link";
import Button from "../../primitives/button";
import { usePickSlip } from "../../pick/create-pick";
import assert from "assert";
import Icon from "../../primitives/icon";
import RadioGroup from "../../primitives/radio-group";
import Checkbox from "../../primitives/checkbox";
import CardBrand from "../../primitives/card-brand";
import Info from "../../primitives/info";

import { useBettor } from "@/utils/providers/bettor";
import { CheckedState } from "@radix-ui/react-checkbox";
import {
  SubscriptionPackage,
  getSubscriptionIntent,
  initiateSubscription,
  isUserSubscribedToPlan,
} from "@/utils/subscription";
import { SubscriptionPricesSchema } from "common";
import Form from "@/components/primitives/form";
import { AuthContext } from "@/app/(public)/drawer";
import Card from "@/components/primitives/card";
import SubscriptionLoginForm from "@/components/auth/subscription-login-form";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
);

interface SubscriptionPlanWithPrice extends SubscriptionPackage {
  selectedPrice?: SubscriptionPricesSchema;
  uniqueId?: string;
}

export interface SubscriptionIntent {
  plan: SubscriptionPackage;
  buyer_bettor_id: number;
  seller_bettor_id: number;
  stripe_client_secret?: string;
  stripe_subscription_id?: string;
  selected_payment_method_id: string | null;
  payment_methods: PaymentMethod[];
  metadata: {
    seller: BettorBasic;
  };
}

export interface SubscriptionCheckoutContextType {
  subscriptionIntent: SubscriptionIntent | null;
  setSubscriptionIntent: React.Dispatch<
    React.SetStateAction<SubscriptionIntent | null>
  >;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  loading: SubscriptionCheckoutStep | null;
  setLoading: React.Dispatch<
    React.SetStateAction<SubscriptionCheckoutStep | null>
  >;
  selectedPaymentRail: PaymentRail | null;
  setSelectedPaymentRail: React.Dispatch<
    React.SetStateAction<PaymentRail | null>
  >;
  waiting: boolean;
  setWaiting: React.Dispatch<React.SetStateAction<boolean>>;
  step: SubscriptionCheckoutStep;
  setStep: React.Dispatch<React.SetStateAction<SubscriptionCheckoutStep>>;
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  subscriptionPackage: SubscriptionPackage | null;
  setSubscriptionPackage: React.Dispatch<
    React.SetStateAction<SubscriptionPackage | null>
  >;
  subscriptionCapperHandle: string | null;
  selectedPlan: SubscriptionPlanWithPrice | null;
  setSelectedPlan: React.Dispatch<
    React.SetStateAction<SubscriptionPlanWithPrice | null>
  >;
}
export const SubscriptionCheckoutContext =
  createContext<SubscriptionCheckoutContextType | null>(null);

export const useSubscriptionCheckout = () => {
  const context = useContext(SubscriptionCheckoutContext);
  if (!context) {
    throw new Error(
      "useSubscriptionCheckout must be used within a SubscriptionCheckoutProvider"
    );
  }
  return context;
};

enum SubscriptionCheckoutStep {
  Subscribe,
  ChangeExtend,
  Review,
  Success,
}

export default function SubscriptionCheckout() {
  const slip = usePickSlip();
  const {
    subscriptionPackage,
    setSubscriptionPackage,
    subscriptionCapperHandle,
  } = slip;
  const [subscriptionIntent, setSubscriptionIntent] =
    React.useState<SubscriptionIntent | null>(null);
  const [loading, setLoading] = React.useState<SubscriptionCheckoutStep | null>(
    null
  );
  const [step, setStep] = React.useState<SubscriptionCheckoutStep>(
    SubscriptionCheckoutStep.Subscribe
  );
  const [waiting, setWaiting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedPaymentRail, setSelectedPaymentRail] =
    React.useState<PaymentRail | null>(null);
  const [paymentMethod, setPaymentMethod] =
    React.useState<PaymentMethod | null>(null);
  const [selectedPlan, setSelectedPlan] =
    React.useState<SubscriptionPlanWithPrice | null>(null);

  React.useEffect(() => {
    if (!subscriptionPackage) {
      console.log(
        "Resetting subscription checkout state - no subscription package"
      );
      setStep(SubscriptionCheckoutStep.Subscribe);
      setSubscriptionIntent(null);
      setSelectedPlan(null);
      setError(null);
      setLoading(null);
      setWaiting(false);
      setSelectedPaymentRail(null);
      setPaymentMethod(null);
    }
  }, [subscriptionPackage]);

  React.useEffect(() => {
    if (error) {
      slip.setButton(
        <Button
          className="!justify-center min-w-[146px]"
          onClick={() => slip.removeCheckoutPick()}
        >
          {slip.justSubscribed ? "Close" : "Cancel"}
        </Button>
      );
    }
    if (loading !== null) {
      slip.setButton(
        <Button className="!justify-center min-w-[146px]" disabled={true}>
          Loading...
        </Button>
      );
    }
    if (waiting) {
      slip.setButton(
        <Button className="!justify-center min-w-[146px]" disabled={true}>
          Processing...
        </Button>
      );
    }
  }, [loading, waiting, error, slip.justSubscribed]);

  const clientSecret = subscriptionIntent?.stripe_client_secret;

  const selectedPrice =
    selectedPlan?.selectedPrice ||
    selectedPlan?.prices?.find(
      (p) =>
        p.id === (selectedPlan as any).default_price_id && p.status === "active"
    ) ||
    selectedPlan?.prices?.find((p) => p.status === "active") ||
    selectedPlan?.prices?.[0];

  const options: StripeElementsOptionsMode = {
    mode: "subscription",
    amount: selectedPrice?.amount_cents || 0,
    currency: "usd",
    paymentMethodCreation: "manual",
    appearance: {
      theme: "flat",
    },
  };

  const render = (): React.ReactNode => {
    if (error) {
      return (
        <div className="flex flex-col gap-4 bg-surface-700 px-4 py-12 rounded-md">
          <Info.Block variant="error" title="Sorry Sport!" message={error} />
        </div>
      );
    }
    switch (step) {
      case SubscriptionCheckoutStep.Subscribe:
        return <Subscribe />;
      case SubscriptionCheckoutStep.Success:
        return <Success />;
      default:
        return <>Something rather unexpected happened. Contact an admin.</>;
    }
  };
  return (
    <SubscriptionCheckoutContext.Provider
      value={{
        subscriptionIntent,
        setSubscriptionIntent,
        error,
        setError,
        loading,
        setLoading,
        selectedPaymentRail,
        setSelectedPaymentRail,
        waiting,
        setWaiting,
        step,
        setStep,
        paymentMethod,
        setPaymentMethod,
        subscriptionPackage,
        setSubscriptionPackage,
        subscriptionCapperHandle,
        selectedPlan,
        setSelectedPlan,
      }}
    >
      <>{render()}</>
    </SubscriptionCheckoutContext.Provider>
  );
}

function Subscribe() {
  const { setAuth } = useContext(AuthContext);
  const slip = usePickSlip();
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { bettor } = useBettor();

  const {
    subscriptionPackage,
    setSubscriptionPackage,
    subscriptionCapperHandle,
    step,
    setStep,
    setSubscriptionIntent,
    subscriptionIntent,
    selectedPlan,
    setSelectedPlan,
    setLoading,
    setError,
    loading,
    waiting,
  } = useSubscriptionCheckout();

  const [allSellerPackages, setAllSellerPackages] = React.useState<
    SubscriptionPlanWithPrice[]
  >([]);
  const [loadingPackages, setLoadingPackages] = React.useState(true);
  const [retryCount, setRetryCount] = React.useState(0);
  const [isManualRetry, setIsManualRetry] = React.useState(false);

  const capperProfile = slip.subscriptionCapperProfile;
  const [isLoadingCapperProfile, setIsLoadingCapperProfile] =
    React.useState(false);

  // Check if user is already subscribed to this package
  React.useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!subscriptionPackage?.id || !isSignedIn) return;

      try {
        const token = await getToken();
        const isAlreadySubscribed = await isUserSubscribedToPlan(
          subscriptionPackage.id,
          token
        );

        if (isAlreadySubscribed) {
          setError(
            "You are already subscribed to this package. Check your purchased subscriptions to manage your subscription."
          );
          return;
        }
      } catch (error) {
        console.error("Error checking subscription status:", error);
      }
    };

    checkSubscriptionStatus();
  }, [subscriptionPackage?.id, isSignedIn, getToken, setError]);

  const retrySubscriptionIntent = () => {
    setIsManualRetry(true);
    setRetryCount(0);
  };

  React.useEffect(() => {
    const fetchMissingCapperProfile = async () => {
      if (
        !capperProfile &&
        subscriptionCapperHandle &&
        !isLoadingCapperProfile
      ) {
        setIsLoadingCapperProfile(true);
        console.log(
          "Fetching missing capper profile for handle:",
          subscriptionCapperHandle
        );

        try {
          const { getCapperProfile } = await import("@/utils/capper");
          const fetchedProfile = await getCapperProfile(
            subscriptionCapperHandle
          );

          if (fetchedProfile?.capper?.id) {
            const capperBasicProfile = {
              id: fetchedProfile.capper.id,
              displayName:
                fetchedProfile.name ||
                fetchedProfile.capper.display_name ||
                fetchedProfile.capper.username ||
                fetchedProfile.handle,
              avatarUrl: fetchedProfile.capper.avatar_url || "",
              totalUnits: 0,
            };

            console.log("Fetched missing capper profile:", capperBasicProfile);
            slip.setSubscriptionCapperProfile(capperBasicProfile);
          }
        } catch (error) {
          console.error("Failed to fetch missing capper profile:", error);
        } finally {
          setIsLoadingCapperProfile(false);
        }
      }
    };

    fetchMissingCapperProfile();
  }, [capperProfile, subscriptionCapperHandle, isLoadingCapperProfile]);

  if (!subscriptionPackage) {
    return (
      <div className="flex flex-col gap-4 bg-surface-700 px-4 py-12 rounded-md">
        <Info.Block
          variant="error"
          title="No Package Selected"
          message="Please select a subscription package from the capper profile."
        />
      </div>
    );
  }

  React.useEffect(() => {
    const fetchPackagePricingTiers = async () => {
      try {
        setLoadingPackages(true);

        if (subscriptionPackage && subscriptionPackage.prices) {
          const activePrices = subscriptionPackage.prices.filter(
            (price) => price.status === "active"
          );
          if (activePrices.length > 0) {
            const packageOptions = activePrices.map((price) => ({
              ...subscriptionPackage,
              selectedPrice: price,
              uniqueId: `${subscriptionPackage.id}-${price.id}`,
              displayName: `${subscriptionPackage.name} - ${
                price.nickname ||
                `${(price.amount_cents / 100).toFixed(2)} / ${price.interval}`
              }${
                price.tier
                  ? ` (${
                      price.tier === "all_access"
                        ? "All Access"
                        : "Limited Access"
                    })`
                  : ""
              }`,
            }));

            setAllSellerPackages(packageOptions);

            const defaultPlan =
              packageOptions.find(
                (p) =>
                  p.selectedPrice?.id ===
                  (subscriptionPackage as any).default_price_id
              ) || packageOptions[0];

            const restoredPlanId = slip.subscriptionSelectedPlanId;
            const restoredPlan = restoredPlanId
              ? packageOptions.find((p) => p.uniqueId === restoredPlanId)
              : null;

            setSelectedPlan(restoredPlan || defaultPlan);
          }
        } else {
          setAllSellerPackages([subscriptionPackage]);
        }
      } catch (error) {
        console.error("Error setting up package pricing options:", error);
        setAllSellerPackages([subscriptionPackage]);
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchPackagePricingTiers();
  }, [subscriptionPackage]);

  React.useEffect(() => {
    const createSubscriptionIntent = async () => {
      if (
        !isLoaded ||
        !isSignedIn ||
        !selectedPlan ||
        subscriptionIntent ||
        step === SubscriptionCheckoutStep.Success
      )
        return;

      try {
        setLoading(SubscriptionCheckoutStep.Subscribe);
        setError(null);
        console.log(
          "Creating subscription intent for plan:",
          selectedPlan.id,
          "Attempt:",
          retryCount + 1
        );
        console.log("Plan details:", {
          id: selectedPlan.id,
          name: selectedPlan.name,
          selectedPrice: selectedPlan.selectedPrice,
          seller_bettor_id: selectedPlan.seller_bettor_id,
        });

        const token = await getToken();
        console.log("Token received, making API call...");

        console.log("Creating subscription intent for plan:", selectedPlan.id);

        // Calculate the selected price or default price from the plan
        const selectedPrice =
          selectedPlan.selectedPrice ||
          selectedPlan.prices?.find(
            (p) =>
              p.id === (selectedPlan as any).default_price_id &&
              p.status === "active"
          ) ||
          selectedPlan.prices?.find((p) => p.status === "active") ||
          selectedPlan.prices?.[0];

        console.log("Price selection debug:", {
          selectedPlanSelectedPrice: selectedPlan.selectedPrice,
          availablePrices: selectedPlan.prices,
          calculatedSelectedPrice: selectedPrice,
          selectedPriceId: selectedPrice?.id,
          selectedPriceStatus: selectedPrice?.status,
        });

        if (!selectedPrice?.id) {
          throw new Error("No price available for subscription");
        }

        console.log("Calling getSubscriptionIntent with:", {
          capperHandle: subscriptionCapperHandle,
          planId: selectedPlan.id,
          priceId: selectedPrice.id,
          priceIdType: typeof selectedPrice.id,
          planIdType: typeof selectedPlan.id,
        });

        if (!subscriptionCapperHandle) {
          throw new Error("No capper handle available for subscription");
        }

        const result = await getSubscriptionIntent(
          subscriptionCapperHandle,
          Number(selectedPlan.id),
          Number(selectedPrice.id),
          token
        );

        console.log("Raw API response from getSubscriptionIntent:", result);

        if (!result) {
          throw new Error("Failed to create subscription intent");
        }

        console.log("Subscription intent validation:", {
          hasResult: !!result,
          hasStripeClientSecret: !!result?.stripe_client_secret,
          resultKeys: result ? Object.keys(result) : [],
          fullResult: result,
        });

        setSubscriptionIntent(result);
        console.log("Subscription intent created successfully:", result);

        setRetryCount(0);
      } catch (error) {
        console.error("Error creating subscription intent:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create subscription intent. Please try again.";
        setError(errorMessage);
      } finally {
        setLoading(null);
        setIsManualRetry(false);
      }
    };

    createSubscriptionIntent();
  }, [
    selectedPlan?.id,
    isLoaded,
    isSignedIn,
    retryCount,
    isManualRetry,
    capperProfile?.id,
    subscriptionIntent,
    step,
  ]);

  const changePlan = (planId: string) => {
    const plan = allSellerPackages.find((p) => p.uniqueId === planId);

    if (plan) {
      setSubscriptionIntent(null);
      setSelectedPlan(plan);

      slip.setSubscriptionSelectedPlanId(planId);

      if (subscriptionPackage && subscriptionCapperHandle) {
        const {
          addSubscriptionQueryParams,
        } = require("@/utils/subscription-storage");
        addSubscriptionQueryParams(
          subscriptionPackage,
          subscriptionCapperHandle,
          planId
        );
      }
    } else {
      console.error("No plan found for uniqueId:", planId);
    }
  };

  const formatInterval = (interval: string, count: number = 1) => {
    const intervalMap = {
      day: count === 1 ? "1 Day" : `${count} Days`,
      week: count === 1 ? "1 Week" : `${count} Weeks`,
      month: count === 1 ? "1 Month" : `${count} Months`,
      year: count === 1 ? "1 Year" : `${count} Years`,
    };

    return (
      intervalMap[interval as keyof typeof intervalMap] ||
      `${count} ${interval}${count > 1 ? "s" : ""}`
    );
  };

  const calculatePricePerDay = (
    amountCents: number,
    interval: string,
    intervalCount: number = 1
  ) => {
    let daysInPeriod = 1;

    switch (interval) {
      case "day":
        daysInPeriod = intervalCount;
        break;
      case "week":
        daysInPeriod = intervalCount * 7;
        break;
      case "month":
        daysInPeriod = intervalCount * 30; // Average month
        break;
      case "year":
        daysInPeriod = intervalCount * 365;
        break;
    }

    const pricePerDayCents = Math.round(amountCents / daysInPeriod);
    return formatAmountCents(pricePerDayCents);
  };

  // Transform packages into pricing tier options
  const planOptions = allSellerPackages.map((pkg) => {
    const priceInfo = pkg.selectedPrice;
    const pricePerDay = priceInfo
      ? calculatePricePerDay(
          priceInfo.amount_cents,
          priceInfo.interval,
          priceInfo.interval_count
        )
      : null;

    return {
      id: pkg.uniqueId!,
      label: (
        <div className="flex justify-between items-center w-full">
          <div>
            <div className="space-y-1 text-left">
              <div className="text-title-lg-bold">
                {priceInfo
                  ? formatInterval(priceInfo.interval, priceInfo.interval_count)
                  : "Monthly"}
              </div>
              {pricePerDay && (
                <div className="text-secondary-500 text-title-lg-bold">
                  ${pricePerDay.whole}.{pricePerDay.decimal}/day
                </div>
              )}
            </div>
          </div>
          <div className="text-title-lg-bold">{`$${
            priceInfo ? formatAmountCents(priceInfo.amount_cents).whole : "0"
          }.${
            priceInfo ? formatAmountCents(priceInfo.amount_cents).decimal : "00"
          }`}</div>
        </div>
      ),
      value: pkg.uniqueId!,
    };
  });

  // Get pricing info for the selected plan
  const selectedPriceInfo =
    selectedPlan?.selectedPrice ||
    selectedPlan?.prices?.find(
      (p) => p.id === (selectedPlan as any).default_price_id
    ) ||
    selectedPlan?.prices?.[0];

  const subtotal = selectedPriceInfo?.amount_cents || 0;
  const serviceFee = 90;
  const total = subtotal + serviceFee;

  const getRenewalDate = () => {
    if (!selectedPlan || !selectedPriceInfo) return "";

    const renewalDate = new Date();
    const { interval, interval_count } = selectedPriceInfo;

    if (interval === "day") {
      renewalDate.setDate(renewalDate.getDate() + interval_count);
    } else if (interval === "week") {
      renewalDate.setDate(renewalDate.getDate() + interval_count * 7);
    } else if (interval === "month") {
      renewalDate.setMonth(renewalDate.getMonth() + interval_count);
    } else if (interval === "year") {
      renewalDate.setFullYear(renewalDate.getFullYear() + interval_count);
    }

    return renewalDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  console.log("Subscription checkout seller info debug:", {
    capperProfile,
    subscriptionIntentSeller: subscriptionIntent?.metadata?.seller,
    subscriptionPackageSellerId: subscriptionPackage.seller_bettor_id,
    isLoadingCapperProfile,
    subscriptionCapperHandle,
  });

  const sellerInfo = capperProfile ||
    subscriptionIntent?.metadata?.seller || {
      displayName: isLoadingCapperProfile
        ? "Loading..."
        : subscriptionCapperHandle || "Capper",
      avatarUrl: "/placeholder/avatar.png",
      id: subscriptionPackage.seller_bettor_id,
    };

  if (loadingPackages) {
    return (
      <div className="flex flex-col gap-4 bg-surface-700 px-4 py-12 rounded-md">
        <Info.Block
          variant="loading"
          title="Loading..."
          message="Setting up pricing options..."
        />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full">
      {/* Loading overlay when waiting */}
      {waiting && (
        <div className="z-50 absolute inset-0 flex justify-center items-center bg-surface-700/90 backdrop-blur-sm">
          <div className="bg-surface-700 shadow-lg p-6 border border-surface-500 rounded-lg">
            <Info.Block
              variant="loading"
              title="Processing..."
              message="Confirming your subscription."
            />
          </div>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex flex-col flex-1 overflow-y-auto">
        <div className="px-6 py-10">
          <div className="flex flex-col items-center gap-4 mb-4">
            <div>
              <img
                src={sellerInfo.avatarUrl}
                alt={sellerInfo.displayName}
                className="rounded-full w-12 h-12 object-cover"
              />
            </div>
            <div className="text-title-lg-bold">{sellerInfo.displayName}</div>
            <div className="text-secondary-300 text-title-sm text-center">
              {subscriptionPackage.name}
            </div>
          </div>{" "}
          {/* <div className="flex flex-col gap-2 mb-12">
            <div className="bg-surface-500 p-3 rounded-lg">
              <div className="flex justify-between items-center w-full">
                <div>
                  <div className="space-y-1">
                    <div className="text-title-lg-bold">
                      {selectedPlan.name}
                    </div>
                    <div className="text-label-sm text-secondary-500">
                      Renews on {getRenewalDate()}
                    </div>
                  </div>
                </div>
                <div className="text-title-lg-bold">{`$${
                  formatAmountCents(selectedPlan.amount_cents).whole
                }`}</div>
              </div>
            </div>
          </div> */}
          <RadioGroup
            label="Select billing frequency"
            items={planOptions}
            onValueChange={changePlan}
            variant="subscription"
            value={selectedPlan?.uniqueId || ""}
          />
        </div>

        <div className="flex-1"></div>

        <div className="px-6 pb-6">
          <div className="bg-surface-500 p-3 rounded-lg">
            <div className="flex justify-between mb-2 text-secondary-300 text-title-sm">
              <div className="">Subtotal</div>
              <div>{`$${formatAmountCents(subtotal).whole}`}</div>
            </div>
            <div className="flex justify-between mb-4 text-secondary-300 text-title-sm">
              <div className="">Service Fee</div>
              <div>{`$${formatAmountCents(serviceFee).whole}`}</div>
            </div>
            <div className="flex justify-between text-secondary-300 text-title-lg-bold">
              <div className="uppercase">Total</div>
              <div>
                <span className="text-title-sm">USD</span>{" "}
                {`$${formatAmountCents(total).whole}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLoaded && isSignedIn && (
        <CustomPaymentFormWrapper selectedPlan={selectedPlan} />
      )}

      {isLoaded && !isSignedIn && subscriptionPackage && (
        <SubscriptionLoginForm
          subscriptionPackage={subscriptionPackage}
          capperProfile={capperProfile || undefined}
          onSuccess={() => {
            // The subscription checkout will re-render when user becomes signed in
            // and the payment form will be shown
          }}
        />
      )}
    </div>
  );
}

function CustomPaymentFormWrapper({
  selectedPlan,
}: {
  selectedPlan: SubscriptionPlanWithPrice | null;
}) {
  if (!selectedPlan) return null;

  // Get pricing info for the wrapper
  const priceInfo =
    selectedPlan.selectedPrice ||
    selectedPlan.prices?.find(
      (p) => p.id === (selectedPlan as any).default_price_id
    ) ||
    selectedPlan.prices?.[0];

  const options: StripeElementsOptionsMode = {
    mode: "subscription",
    amount: priceInfo?.amount_cents || 0,
    currency: "usd",
    paymentMethodCreation: "manual",
    appearance: {
      theme: "flat",
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CustomPaymentForm />
    </Elements>
  );
}

function CustomPaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const { getToken } = useAuth();
  const slip = usePickSlip();
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [useExistingPayment, setUseExistingPayment] = React.useState(false);
  const [cardNumberFocused, setCardNumberFocused] = React.useState(false);
  const [cardExpiryFocused, setCardExpiryFocused] = React.useState(false);
  const [cardCvcFocused, setCardCvcFocused] = React.useState(false);
  const [cardNumberComplete, setCardNumberComplete] = React.useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = React.useState(false);
  const [cardCvcComplete, setCardCvcComplete] = React.useState(false);
  const [cardNumberValid, setCardNumberValid] = React.useState(false);
  const [cardExpiryValid, setCardExpiryValid] = React.useState(false);
  const [cardCvcValid, setCardCvcValid] = React.useState(false);

  const {
    subscriptionIntent,
    setSubscriptionIntent,
    setStep,
    setError,
    setWaiting,
    selectedPlan,
    subscriptionCapperHandle,
  } = useSubscriptionCheckout();

  React.useEffect(() => {
    if (subscriptionIntent) {
      const hasExistingPaymentMethods =
        subscriptionIntent.payment_methods &&
        subscriptionIntent.payment_methods.length > 0;

      if (hasExistingPaymentMethods) {
        setUseExistingPayment(true);
      }
    }
  }, [subscriptionIntent]);
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service to continue.");
      return;
    }

    if (!selectedPlan) {
      setError("No subscription plan selected.");
      return;
    }

    if (!subscriptionIntent) {
      console.error("Subscription intent validation failed:", {
        hasSubscriptionIntent: !!subscriptionIntent,
        subscriptionIntent: subscriptionIntent,
      });
      setError("Subscription intent not ready. Please try again.");
      return;
    }

    console.log("Subscription intent ready:", {
      hasSubscriptionIntent: !!subscriptionIntent,
      hasClientSecret: !!subscriptionIntent?.stripe_client_secret,
      paymentMethods: subscriptionIntent?.payment_methods?.length || 0,
    });

    setWaiting(true);
    setError("");

    // Immediately update the slip button to show processing state
    slip.setButton(
      <Button className="!justify-center min-w-[146px]" disabled={true}>
        Processing...
      </Button>
    );

    try {
      let paymentMethodId = "";

      if (useExistingPayment && subscriptionIntent?.payment_methods?.length) {
        // Use the default payment method or the first available one
        const existingPaymentMethod =
          subscriptionIntent.payment_methods.find(
            (pm) => pm.id === subscriptionIntent.selected_payment_method_id
          ) || subscriptionIntent.payment_methods[0];
        paymentMethodId = existingPaymentMethod.id;
        console.log("Using existing payment method:", paymentMethodId);
      } else {
        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);

        if (!cardNumberElement) {
          throw new Error("Card number element not found");
        }

        console.log("Creating new payment method with Stripe...");

        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
            billing_details: {},
          });

        if (paymentMethodError) {
          console.error("Stripe payment method error:", paymentMethodError);
          setError(
            paymentMethodError.message ||
              "Failed to create payment method. Please try again."
          );
          return;
        }

        if (!paymentMethod) {
          throw new Error("Failed to create payment method");
        }

        paymentMethodId = paymentMethod.id;
        console.log("Payment method created successfully:", paymentMethodId);
      }

      console.log("Initiating subscription on backend...");
      const token = await getToken();

      // Calculate the selected price or default price from the plan
      const selectedPrice =
        selectedPlan.selectedPrice ||
        selectedPlan.prices?.find(
          (p) =>
            p.id === (selectedPlan as any).default_price_id &&
            p.status === "active"
        ) ||
        selectedPlan.prices?.find((p) => p.status === "active") ||
        selectedPlan.prices?.[0];

      if (!selectedPrice?.id) {
        throw new Error("No price available for subscription");
      }

      const subscriptionResult = await initiateSubscription(
        {
          capper_handle: subscriptionCapperHandle!,
          plan_id: selectedPlan.id,
          price_id: selectedPrice.id,
          payment_method_id: paymentMethodId,
        },
        token
      );

      if (!subscriptionResult) {
        throw new Error("Failed to initiate subscription");
      }

      console.log("Subscription initiated successfully:", subscriptionResult);

      if (subscriptionResult.stripe_client_secret) {
        console.log(
          "Subscription requires payment confirmation, confirming..."
        );

        const { error: confirmError } = await stripe.confirmPayment({
          clientSecret: subscriptionResult.stripe_client_secret,
          confirmParams: {
            payment_method: paymentMethodId,
          },
          redirect: "if_required",
        });

        if (confirmError) {
          console.error("Stripe confirmation error:", confirmError);
          setError(
            confirmError.message ||
              "Payment confirmation failed. Please try again."
          );
          return;
        }

        console.log("Payment confirmed successfully");
      }

      setSubscriptionIntent(subscriptionResult);
      setStep(SubscriptionCheckoutStep.Success);
    } catch (error) {
      console.error("Payment submission error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Payment failed. Please check your card details and try again.";
      setError(errorMessage);
    } finally {
      setWaiting(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#232528",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        "::placeholder": {
          color: "#A3A8B1",
        },
      },
      invalid: {
        color: "#ef4444",
        iconColor: "#ef4444",
      },
    },
  };

  const isCombinedFieldFocused =
    cardNumberFocused || cardExpiryFocused || cardCvcFocused;
  const hasAnyContent =
    cardNumberComplete || cardExpiryComplete || cardCvcComplete;
  const areAllFieldsValid = cardNumberValid && cardExpiryValid && cardCvcValid;

  const hasExistingPaymentMethods =
    subscriptionIntent?.payment_methods &&
    subscriptionIntent.payment_methods.length > 0;
  const isFormValid =
    agreedToTerms && (useExistingPayment ? true : areAllFieldsValid);

  return (
    <div className="bg-surface-700 p-6 border-surface-100 border-t">
      <div className="mb-5 text-title-lg-bold uppercase">Payment</div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        <Form.Field name="terms" className="">
          <Checkbox
            id="agree-terms"
            className=""
            label={
              <div className="text-secondary-300 text-title-lg-medium">
                Agree to{" "}
                <Link
                  href="/terms-conditions"
                  className="underline"
                  target="_blank"
                >
                  Terms of Service
                </Link>
              </div>
            }
            checked={agreedToTerms as CheckedState}
            onCheckedChange={(checked) =>
              setAgreedToTerms(checked === true || checked === "indeterminate")
            }
          />
        </Form.Field>

        {hasExistingPaymentMethods && (
          <div className="space-y-4">
            <div className="text-label text-secondary-300">Payment Method</div>

            {useExistingPayment ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center bg-surface-600 p-4 border border-input-grey rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CardBrand
                      brand={subscriptionIntent.payment_methods[0].brand}
                    />
                    <div className="flex flex-col">
                      <span className="text-secondary-300 text-title-lg-medium">
                        •••• {subscriptionIntent.payment_methods[0].last4}
                      </span>
                      <span className="text-body-sm text-secondary-400">
                        Expires{" "}
                        {subscriptionIntent.payment_methods[0].exp_month}/
                        {subscriptionIntent.payment_methods[0].exp_year}
                      </span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="underline"
                    onClick={() => setUseExistingPayment(false)}
                    className="text-primary hover:text-primary-hover"
                  >
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-secondary-300 text-title-lg-medium">
                    Add new payment method
                  </span>
                  {subscriptionIntent.payment_methods.length > 0 && (
                    <Button
                      type="button"
                      variant="underline"
                      onClick={() => setUseExistingPayment(true)}
                      className="text-primary hover:text-primary-hover"
                    >
                      Use saved card
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {(!hasExistingPaymentMethods || !useExistingPayment) && (
          <div className="group relative">
            <div
              className={`text-label text-secondary-300 bg-surface-700 py-0.5 px-1 absolute left-2 -top-2 z-[1] rounded-sm transition-all duration-100 `}
            >
              Card Details
            </div>

            <div
              className={`group w-full border hover:border-dark focus:border-dark text-input text-secondary-300 py-3 px-4 rounded-md h-[3.25rem] flex items-center outline-none bg-surface-700 transition-all duration-100 ease-in-out ${
                isCombinedFieldFocused
                  ? "border-dark"
                  : "border-input-grey hover:border-dark"
              }`}
            >
              <div className="flex-1 min-w-0">
                <CardNumberElement
                  options={cardElementOptions}
                  onFocus={() => setCardNumberFocused(true)}
                  onBlur={() => setCardNumberFocused(false)}
                  onChange={(event) => {
                    setCardNumberComplete(event.complete);
                    setCardNumberValid(!event.error && event.complete);
                    if (event.complete && !event.error && elements) {
                      const expiryElement =
                        elements.getElement(CardExpiryElement);
                      if (expiryElement) {
                        expiryElement.focus();
                      }
                    }
                  }}
                />
              </div>

              <div className="w-20">
                <CardExpiryElement
                  options={cardElementOptions}
                  onFocus={() => setCardExpiryFocused(true)}
                  onBlur={() => setCardExpiryFocused(false)}
                  onChange={(event) => {
                    setCardExpiryComplete(event.complete);
                    setCardExpiryValid(!event.error && event.complete);
                    if (event.complete && !event.error && elements) {
                      const cvcElement = elements.getElement(CardCvcElement);
                      if (cvcElement) {
                        cvcElement.focus();
                      }
                    }
                  }}
                />
              </div>

              <div className="w-12">
                <CardCvcElement
                  options={cardElementOptions}
                  onFocus={() => setCardCvcFocused(true)}
                  onBlur={() => setCardCvcFocused(false)}
                  onChange={(event) => {
                    setCardCvcComplete(event.complete);
                    setCardCvcValid(!event.error && event.complete);

                    if (
                      event.complete &&
                      !event.error &&
                      submitButtonRef.current
                    ) {
                      submitButtonRef.current.focus();
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <Button
          ref={submitButtonRef}
          type="submit"
          className="!justify-center w-full"
          disabled={!stripe || !isFormValid}
        >
          SUBSCRIBE
        </Button>

        <div className="flex justify-center">
          <div className="flex items-center gap-2">
            <span className="text-secondary-400 text-title-sm-medium">
              Powered by
            </span>
            <span className="font-semibold">
              <svg
                width="44"
                height="19"
                viewBox="0 0 44 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M43.9947 9.81436C43.9947 6.56824 42.4792 4.00684 39.5827 4.00684C36.674 4.00684 34.9141 6.56823 34.9141 9.789C34.9141 13.6057 36.9917 15.5331 39.9738 15.5331C41.4281 15.5331 42.5281 15.1908 43.3591 14.7089V12.1729C42.5281 12.604 41.5748 12.8703 40.3649 12.8703C39.1794 12.8703 38.1283 12.4392 37.9939 10.9429H43.9702C43.9702 10.7781 43.9947 10.1187 43.9947 9.81436ZM37.9572 8.60975C37.9572 7.17688 38.8005 6.58092 39.5705 6.58092C40.316 6.58092 41.1104 7.17688 41.1104 8.60975H37.9572Z"
                  fill="#6D7582"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M30.1962 4.00684C28.9985 4.00684 28.2285 4.59013 27.8007 4.99589L27.6419 4.20972H24.9531V18.9948L28.0085 18.3228L28.0207 14.7343C28.4607 15.064 29.1084 15.5331 30.1839 15.5331C32.3716 15.5331 34.3637 13.7072 34.3637 9.68756C34.3515 6.01031 32.3349 4.00684 30.1962 4.00684ZM29.4629 12.7435C28.7418 12.7435 28.314 12.4772 28.0207 12.1475L28.0085 7.44317C28.3263 7.07544 28.7662 6.82184 29.4629 6.82184C30.575 6.82184 31.345 8.11522 31.345 9.77632C31.345 11.4755 30.5873 12.7435 29.4629 12.7435Z"
                  fill="#6D7582"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M20.75 3.25881L23.8176 2.57408V0L20.75 0.67205V3.25881Z"
                  fill="#6D7582"
                />
                <path
                  d="M23.8176 4.22266H20.75V15.3178H23.8176V4.22266Z"
                  fill="#6D7582"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M17.4682 5.1612L17.2727 4.22287H14.6328V15.318H17.6882V7.79868C18.4093 6.82231 19.6314 6.99983 20.0103 7.13931V4.22287C19.6192 4.0707 18.1893 3.79174 17.4682 5.1612Z"
                  fill="#6D7582"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M11.3537 1.47095L8.3716 2.13032L8.35938 12.2872C8.35938 14.1638 9.71597 15.546 11.5248 15.546C12.5269 15.546 13.2602 15.3558 13.6635 15.1275V12.5534C13.2724 12.7183 11.3414 13.3016 11.3414 11.4249V6.92343H13.6635V4.22255H11.3414L11.3537 1.47095Z"
                  fill="#6D7582"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M3.09205 7.44329C3.09205 6.94876 3.48314 6.75856 4.13088 6.75856C5.05972 6.75856 6.23299 7.0502 7.16182 7.57009V4.59025C6.14744 4.1718 5.14527 4.00696 4.13088 4.00696C1.64991 4.00696 0 5.35106 0 7.59545C0 11.0952 4.64419 10.5373 4.64419 12.0462C4.64419 12.6295 4.15533 12.8197 3.47092 12.8197C2.45653 12.8197 1.16105 12.3886 0.134437 11.8053V14.8232C1.27104 15.3304 2.41987 15.5459 3.47092 15.5459C6.013 15.5459 7.76068 14.2399 7.76068 11.9701C7.74846 8.19142 3.09205 8.86347 3.09205 7.44329Z"
                  fill="#6D7582"
                />
              </svg>
            </span>
          </div>
        </div>
      </Form>
    </div>
  );
}

function Success() {
  const {
    selectedPlan,
    subscriptionIntent,
    setStep,
    setSubscriptionIntent,
    setSelectedPlan,
    setError,
    setLoading,
    setWaiting,
    setSelectedPaymentRail,
    setPaymentMethod,
  } = useSubscriptionCheckout();
  const slip = usePickSlip();
  const { getToken } = useAuth();
  const [isGeneratingDiscordInvite, setIsGeneratingDiscordInvite] =
    useState(false);

  // Calculate renewal date and pricing info
  const selectedPrice =
    selectedPlan?.selectedPrice ||
    selectedPlan?.prices?.find(
      (p) =>
        p.id === (selectedPlan as any).default_price_id && p.status === "active"
    ) ||
    selectedPlan?.prices?.find((p) => p.status === "active") ||
    selectedPlan?.prices?.[0];

  const getRenewalDate = () => {
    if (!selectedPrice) return "";

    const renewalDate = new Date();
    const { interval, interval_count } = selectedPrice;

    if (interval === "day") {
      renewalDate.setDate(renewalDate.getDate() + interval_count);
    } else if (interval === "week") {
      renewalDate.setDate(renewalDate.getDate() + interval_count * 7);
    } else if (interval === "month") {
      renewalDate.setMonth(renewalDate.getMonth() + interval_count);
    } else if (interval === "year") {
      renewalDate.setFullYear(renewalDate.getFullYear() + interval_count);
    }

    return renewalDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleOpenDiscord = async () => {
    if (!subscriptionIntent?.stripe_subscription_id) {
      console.error("No subscription ID available for Discord invite");
      return;
    }

    setIsGeneratingDiscordInvite(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/discord/subscriptions/${subscriptionIntent.stripe_subscription_id}/invite`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: { ok: boolean; invite: string } = await response.json();

      if (result.ok && result.invite) {
        window.open(result.invite, "_blank");
      } else {
        console.error("Failed to generate Discord invite:", result);
      }
    } catch (error) {
      console.error("Error generating Discord invite:", error);
    } finally {
      setIsGeneratingDiscordInvite(false);
    }
  };

  React.useEffect(() => {
    slip.setJustSubscribed(true);

    // Optimistically update the subscription status in the parent component
    // by dispatching a custom event that the capper profile can listen to
    const notifySubscriptionSuccess = () => {
      const event = new CustomEvent("subscriptionSuccess", {
        detail: {
          planId: selectedPlan?.id,
          planName: selectedPlan?.name,
          subscriptionId: subscriptionIntent?.stripe_subscription_id,
        },
      });
      window.dispatchEvent(event);
    };

    // Notify immediately and also after a short delay to ensure proper timing
    notifySubscriptionSuccess();

    slip.setButton(
      <Button
        className="!justify-center min-w-[146px]"
        onClick={() => {
          notifySubscriptionSuccess();

          slip.clearSubscriptionState();
          slip.setSubscriptionPackage(null);
          slip.setSubscriptionCapperHandle(null);
          slip.setSubscriptionCapperProfile(null);
          slip.setSubscriptionSelectedPlanId(null);
          slip.setJustSubscribed(false);

          setStep(SubscriptionCheckoutStep.Subscribe);
          setSubscriptionIntent(null);
          setSelectedPlan(null);
          setError(null);
          setLoading(null);
          setWaiting(false);
          setSelectedPaymentRail(null);
          setPaymentMethod(null);

          if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.searchParams.delete("subscription_package_id");
            url.searchParams.delete("capper_handle");
            url.searchParams.delete("selected_plan_id");

            window.history.replaceState({}, "", url.toString());
          }

          slip.setSlipOpen(false);
        }}
      >
        Done
      </Button>
    );
  }, [
    selectedPlan?.id,
    selectedPlan?.name,
    subscriptionIntent?.stripe_subscription_id,
  ]);

  return (
    <div className="bg-surface-700 p-6">
      <div className="relative flex flex-col gap-4 bg-secondary-300 px-3 sm:px-5 py-6 rounded-xl overflow-hidden text-surface-700">
        <div className="z-10 absolute inset-0">
          <video
            src="/blob-430x430.mp4"
            playsInline
            muted
            autoPlay
            loop
            className="w-full h-full object-bottom object-cover"
          ></video>
        </div>

        <div className="z-20 relative flex flex-col gap-6">
          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-center items-center bg-[#0A9B0033] rounded-full w-20 h-20">
              <Icon name="checkmark" className="fill-success-300" size="xl" />
            </div>
            <div className="text-display text-white uppercase">Subscribed!</div>
          </div>

          {selectedPlan && selectedPrice && (
            <div className="space-y-4">
              <Card dark>
                <div className="mb-4 text-body text-white text-center">
                  You have successfully subscribed to{" "}
                  <span className="font-bold">
                    {slip.subscriptionCapperProfile?.displayName ||
                      subscriptionIntent?.metadata?.seller?.displayName ||
                      slip.subscriptionCapperHandle ||
                      "this capper"}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center text-white">
                    <span className="text-body">Plan:</span>
                    <span className="font-medium">{selectedPlan.name}</span>
                  </div>

                  <div className="flex justify-between items-center text-white">
                    <span className="text-body">Price:</span>
                    <span className="font-medium">
                      ${formatAmountCents(selectedPrice.amount_cents).whole}.
                      {formatAmountCents(selectedPrice.amount_cents).decimal}/
                      {selectedPrice.interval_count > 1
                        ? `${selectedPrice.interval_count} `
                        : ""}
                      {selectedPrice.interval}
                      {selectedPrice.interval_count > 1 ? "s" : ""}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-white">
                    <span className="text-body">Next Renewal:</span>
                    <span className="font-medium">{getRenewalDate()}</span>
                  </div>
                </div>
                <div className="flex justify-center mt-4">
                  <Button
                    type="button"
                    color="primary"
                    variant="standard"
                    href="/account/purchased-subs"
                  >
                    Manage Subscriptions
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {selectedPlan && !selectedPrice && (
            <Card dark>
              <div className="text-body text-white text-center">
                You have successfully subscribed to the{" "}
                <span className="font-bold">{selectedPlan.name}</span> plan.
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
